import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

const PnL = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [selectedAccount, setSelectedAccount] = useState('all')
  const [loading, setLoading] = useState(true)
  const [pnlData, setPnlData] = useState([])
  const [monthlyData, setMonthlyData] = useState([])
  const [metrics, setMetrics] = useState({
    totalPnL: 0,
    todayPnL: 0,
    winRate: 0,
    totalTrades: 0,
    avgWin: 0,
    avgLoss: 0,
    profitFactor: 0
  })
  const { user } = useAuth()

  useEffect(() => {
    const fetchPnLData = async () => {
      try {
        setLoading(true)
        
        // Fetch trades for the selected period
        const tradesResponse = await api.trades.getAll({ 
          status: 'closed',
          limit: 1000 
        })
        const trades = tradesResponse.data.trades || []

        // Fetch analytics
        const analyticsResponse = await api.analytics.getOverview({ period: selectedPeriod })
        setMetrics(analyticsResponse.data.metrics || {
          totalPnL: 0,
          todayPnL: 0,
          winRate: 0,
          totalTrades: 0,
          avgWin: 0,
          avgLoss: 0,
          profitFactor: 0
        })

        // Process trades into daily P&L data
        const dailyPnL = {}
        let cumulativePnL = 0

        trades.forEach(trade => {
          if (trade.pnl && trade.exit_time) {
            const date = new Date(trade.exit_time).toISOString().split('T')[0]
            if (!dailyPnL[date]) {
              dailyPnL[date] = 0
            }
            dailyPnL[date] += trade.pnl
          }
        })

        // Convert to chart data
        const chartData = Object.entries(dailyPnL)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .map(([date, daily]) => {
            cumulativePnL += daily
            return {
              date,
              daily,
              cumulative: cumulativePnL
            }
          })

        setPnlData(chartData)

        // Process monthly data
        const monthlyPnL = {}
        trades.forEach(trade => {
          if (trade.pnl && trade.exit_time) {
            const date = new Date(trade.exit_time)
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            const monthName = date.toLocaleDateString('en-US', { month: 'short' })
            
            if (!monthlyPnL[monthKey]) {
              monthlyPnL[monthKey] = { month: monthName, pnl: 0 }
            }
            monthlyPnL[monthKey].pnl += trade.pnl
          }
        })

        const monthlyChartData = Object.values(monthlyPnL)
          .sort((a, b) => a.month.localeCompare(b.month))

        setMonthlyData(monthlyChartData)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching P&L data:', error)
        setLoading(false)
      }
    }

    if (user) {
      fetchPnLData()
    }
  }, [user, selectedPeriod])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  if (loading) {
    return (
      <div className="relative min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-full">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-rose-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-pink-200 to-rose-200 bg-clip-text text-transparent">
                Profit & Loss
              </h1>
              <p className="text-gray-300 mt-1">
                Track your trading performance and profitability
              </p>
            </div>
            
            {/* Period Selector */}
            <div className="flex items-center space-x-2">
              {['7d', '30d', '90d', '1y'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedPeriod === period
                      ? 'bg-pink-600 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {period === '7d' ? '7 Days' : 
                   period === '30d' ? '30 Days' : 
                   period === '90d' ? '90 Days' : '1 Year'}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Key Metrics */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total P&L</p>
                  <p className={`text-2xl font-bold mt-1 ${(metrics.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(metrics.totalPnL || 0)}
                  </p>
                  <div className={`flex items-center mt-2 text-sm ${(metrics.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {(metrics.totalPnL || 0) >= 0 ? <ArrowTrendingUpIcon className="w-4 h-4 mr-1" /> : <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />}
                    <span>All time</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl">
                  <BanknotesIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Win Rate</p>
                  <p className="text-2xl font-bold text-white mt-1">{(metrics.winRate || 0).toFixed(1)}%</p>
                  <div className={`flex items-center mt-2 text-sm ${(metrics.winRate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(metrics.winRate || 0) >= 50 ? 'Above average' : 'Below average'}</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                  <ChartBarIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Avg Win</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(metrics.avgWin || 0)}</p>
                  <div className="flex items-center mt-2 text-sm text-gray-400">
                    <span>Per winning trade</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                  <ArrowUpIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Avg Loss</p>
                  <p className="text-2xl font-bold text-red-400 mt-1">{formatCurrency(Math.abs(metrics.avgLoss || 0))}</p>
                  <div className="flex items-center mt-2 text-sm text-gray-400">
                    <span>Per losing trade</span>
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl">
                  <ArrowTrendingDownIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Daily P&L Chart */}
            <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Daily P&L</h3>
              {pnlData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={pnlData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="rgba(255,255,255,0.5)"
                      fontSize={12}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis 
                      stroke="rgba(255,255,255,0.5)"
                      fontSize={12}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                      formatter={(value, name) => [`$${value.toFixed(2)}`, name === 'daily' ? 'Daily P&L' : 'Cumulative P&L']}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="daily" 
                      stroke="#ec4899" 
                      strokeWidth={2}
                      dot={{ fill: '#ec4899', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#ec4899', strokeWidth: 2 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cumulative" 
                      stroke="#06b6d4" 
                      strokeWidth={2}
                      dot={{ fill: '#06b6d4', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#06b6d4', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400">
                  <div className="text-center">
                    <ChartBarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No P&L data available</p>
                    <p className="text-sm mt-2">Start trading to see your performance</p>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Monthly P&L Chart */}
            <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Monthly P&L</h3>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="rgba(255,255,255,0.5)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="rgba(255,255,255,0.5)"
                      fontSize={12}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Monthly P&L']}
                    />
                    <Bar 
                      dataKey="pnl" 
                      fill="url(#monthlyGradient)"
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="monthlyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#be185d" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400">
                  <div className="text-center">
                    <ChartBarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No monthly data available</p>
                    <p className="text-sm mt-2">Complete some trades to see monthly breakdown</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Performance Summary */}
          <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-6">Performance Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{metrics.totalTrades || 0}</div>
                <div className="text-gray-300">Total Trades</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">{(metrics.profitFactor || 0).toFixed(2)}</div>
                <div className="text-gray-300">Profit Factor</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${(metrics.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {((metrics.totalPnL || 0) / 10000 * 100).toFixed(1)}%
                </div>
                <div className="text-gray-300">Total Return</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default PnL